import type { DataTableRow } from "@client-shared";
import type { Row, RowSelectionState, Table } from "@tanstack/react-table";
import * as React from "react";
import { SelectionPopoverTrigger } from "./selection-popover-trigger";

interface HeaderSelectionPopoverProps<T extends object> {
  table: Table<DataTableRow<T>>;
  setRowSelection: (selection: RowSelectionState) => void;
  maxSelectableRows?: number;
}

export const HeaderSelectionPopover = <T extends object>({ table, setRowSelection, maxSelectableRows }: HeaderSelectionPopoverProps<T>) => {
  const prePaginationFlatRows = table.getPrePaginationRowModel().flatRows;

  const allLeafRows = React.useMemo(
    () => prePaginationFlatRows.filter((row) => !row.getIsGrouped() && !row.original.isDisabled),
    [prePaginationFlatRows]
  );

  const { pageIndex, pageSize } = table.getState().pagination;
  const expandedFlatRows = table.getExpandedRowModel().flatRows;

  const currentPageLeafRows = React.useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    const pageSlice = expandedFlatRows.slice(start, end);
    return pageSlice.filter((row) => !row.getIsGrouped() && !row.original.isDisabled);
  }, [expandedFlatRows, pageIndex, pageSize]);

  const selection = table.getState().rowSelection;
  const selectedCount = React.useMemo(() => allLeafRows.filter((row) => selection[row.id] === true).length, [allLeafRows, selection]);

  const effectiveMax = maxSelectableRows ? Math.min(allLeafRows.length, maxSelectableRows) : allLeafRows.length;

  const handleApplySelection = React.useCallback(
    (mode: "all" | "currentPage" | "specific", quantity?: number) => {
      const newSelection: Record<string, boolean> = {};

      let rowsToSelect: Row<DataTableRow<T>>[];
      switch (mode) {
        case "all":
          rowsToSelect = allLeafRows.slice(0, effectiveMax);
          break;
        case "currentPage":
          rowsToSelect = currentPageLeafRows.slice(0, effectiveMax);
          break;
        case "specific": {
          const count = Math.min(Math.max(0, quantity ?? 0), effectiveMax);
          rowsToSelect = allLeafRows.slice(0, count);
          break;
        }
        default:
          rowsToSelect = [];
      }

      for (const leafRow of rowsToSelect) {
        newSelection[leafRow.id] = true;
      }

      setRowSelection(newSelection);
    },
    [allLeafRows, currentPageLeafRows, effectiveMax, setRowSelection]
  );

  const handleClear = React.useCallback(() => {
    setRowSelection({});
  }, [setRowSelection]);

  return (
    <SelectionPopoverTrigger
      testIdComponent="HeaderSelectionPopover"
      selectedCount={selectedCount}
      allLeafCount={effectiveMax}
      currentPageCount={Math.min(currentPageLeafRows.length, effectiveMax)}
      maxLimit={effectiveMax}
      showMaxLabel={allLeafRows.length > (maxSelectableRows ?? allLeafRows.length)}
      onApplySelection={handleApplySelection}
      onClear={handleClear}
      className="text-primary font-semibold"
    />
  );
};
